<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import AiAgentsAPI from '../../../../../api/aiAgents';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['saved']);

const store = useStore();
const { t } = useI18n();
const alert = useAlert;
const form = ref({});
const apiToken = ref('');
const hasExistingToken = computed(() => !!props.agent?.has_api_token);
const tokenPlaceholder = computed(() =>
  hasExistingToken.value ? '••••••••••••••••' : ''
);
const isVerifying = ref(false);
const verifyMessage = ref('');
const verifySuccess = ref(false);

const resetForm = () => {
  const cloned = JSON.parse(JSON.stringify(props.agent));
  form.value = {
    ...cloned,
    temperature: Number(cloned.temperature ?? 0.3),
    llm_provider: cloned.llm_provider || 'openai',
    llm_model: cloned.llm_model || '',
    catalog_type: cloned.catalog_type || 'products',
    config: {
      max_tokens: cloned.config?.max_tokens || 2048,
      ...cloned.config,
    },
  };
};

watch(() => props.agent, resetForm, { immediate: true, deep: true });

const providers = [
  { value: 'openai', label: 'OpenAI', icon: 'i-lucide-brain' },
];

const openaiModelOptions = [
  { value: 'gpt-5-nano', label: 'GPT-5 nano' },
  { value: 'gpt-5-mini', label: 'GPT-5 mini' },
  { value: 'gpt-5', label: 'GPT-5' },
];

const openaiModels = [
  {
    value: 'gpt-5-nano',
    label: 'GPT-5 nano',
    costStars: 1,
    costLabel: 'COST_VERY_LOW',
    speedStars: 5,
    taskKey: 'TASK_MESSAGE_CLASSIFICATION',
  },
  {
    value: 'gpt-5-nano',
    label: 'GPT-5 nano',
    costStars: 1,
    costLabel: 'COST_VERY_LOW',
    speedStars: 5,
    taskKey: 'TASK_DATA_EXTRACTION',
  },
  {
    value: 'gpt-5-mini',
    label: 'GPT-5 mini',
    costStars: 1,
    costLabel: 'COST_LOW',
    speedStars: 4,
    taskKey: 'TASK_SIMPLE_RESPONSES',
  },
  {
    value: 'gpt-5-mini',
    label: 'GPT-5 mini',
    costStars: 1,
    costLabel: 'COST_LOW',
    speedStars: 4,
    taskKey: 'TASK_CONVERSATION_SUMMARY',
  },
  {
    value: 'gpt-5-mini',
    label: 'GPT-5 mini',
    costStars: 1,
    costLabel: 'COST_LOW',
    speedStars: 4,
    taskKey: 'TASK_EMAIL_WRITING',
  },
  {
    value: 'gpt-5',
    label: 'GPT-5',
    costStars: 2,
    costLabel: 'COST_MEDIUM',
    speedStars: 3,
    taskKey: 'TASK_CUSTOMER_SUPPORT',
  },
  {
    value: 'gpt-5',
    label: 'GPT-5',
    costStars: 2,
    costLabel: 'COST_MEDIUM',
    speedStars: 3,
    taskKey: 'TASK_SALES_AUTOMATION',
  },
  {
    value: 'gpt-5',
    label: 'GPT-5',
    costStars: 2,
    costLabel: 'COST_MEDIUM',
    speedStars: 3,
    taskKey: 'TASK_DOCUMENT_ANALYSIS',
  },
];

const availableModels = computed(() => {
  return openaiModelOptions;
});

const onProviderChange = () => {
  const models = availableModels.value;
  if (models.length && !models.find(m => m.value === form.value.llm_model)) {
    form.value.llm_model = models[0].value;
  }
};

const verifyToken = async () => {
  isVerifying.value = true;
  verifyMessage.value = '';
  try {
    const { data } = await AiAgentsAPI.verifyToken(
      props.agent.id,
      apiToken.value,
      form.value.llm_provider
    );
    verifySuccess.value = data.valid;
    verifyMessage.value = data.valid
      ? t('AI_AGENTS.MESSAGES.VERIFY_TOKEN_SUCCESS')
      : t('AI_AGENTS.MESSAGES.VERIFY_TOKEN_ERROR');
  } catch (error) {
    verifySuccess.value = false;
    verifyMessage.value = t('AI_AGENTS.MESSAGES.VERIFY_TOKEN_ERROR');
  } finally {
    isVerifying.value = false;
  }
};

const save = async () => {
  try {
    await store.dispatch('aiAgents/update', {
      id: props.agent.id,
      data: { ...form.value, api_token: apiToken.value },
    });
    alert(t('AI_AGENTS.MESSAGES.UPDATE_SUCCESS'));
    emit('saved');
  } catch (error) {
    alert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  }
};
</script>

<template>
  <div class="grid grid-cols-1 gap-6">
    <!-- SECCIÓN: PROVEEDOR Y MODELO -->
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <div class="xl:col-span-2 flex items-center gap-2 mb-1">
        <span class="i-lucide-brain w-4 h-4 text-n-brand" />
        <span
          class="text-xs font-bold uppercase tracking-wider text-n-slate-10"
        >
          {{ t('AI_AGENTS.CONFIG.SECTION_PROVIDER') }}
        </span>
        <div class="flex-1 h-px bg-n-slate-6" />
      </div>

      <Accordion
        icon="i-lucide-server"
        :title="t('AI_AGENTS.CONFIG.PROVIDER_TITLE')"
        :description="t('AI_AGENTS.CONFIG.PROVIDER_DESCRIPTION')"
        is-open
      >
        <div class="grid gap-4">
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.FORM.LLM_PROVIDER')
            }}</span>
            <select
              v-model="form.llm_provider"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              @change="onProviderChange"
            >
              <option
                v-for="provider in providers"
                :key="provider.value"
                :value="provider.value"
              >
                {{ provider.label }}
              </option>
            </select>
          </label>

          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.FORM.LLM_MODEL')
            }}</span>
            <select
              v-model="form.llm_model"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            >
              <option
                v-for="model in availableModels"
                :key="model.value"
                :value="model.value"
              >
                {{ model.label }}
              </option>
            </select>
          </label>
        </div>
      </Accordion>

      <Accordion
        icon="i-lucide-key"
        :title="t('AI_AGENTS.CONFIG.TOKEN_TITLE')"
        :description="t('AI_AGENTS.CONFIG.TOKEN_DESCRIPTION')"
      >
        <div class="grid gap-1">
          <span class="text-sm font-medium">{{
            t('AI_AGENTS.FORM.API_TOKEN')
          }}</span>
          <input
            v-model="apiToken"
            type="password"
            :placeholder="tokenPlaceholder"
            class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
          />
          <span
            v-if="hasExistingToken && !apiToken"
            class="text-xs text-n-emerald-11 flex items-center gap-1"
          >
            <span class="i-lucide-check-circle w-3 h-3" />
            {{ t('AI_AGENTS.FORM.TOKEN_SAVED') }}
          </span>
          <div class="flex items-center gap-2 mt-1">
            <button
              class="px-3 py-1 text-xs font-medium rounded bg-n-brand text-white hover:bg-n-brand/90 disabled:opacity-50"
              :disabled="isVerifying || !apiToken"
              @click="verifyToken"
            >
              {{
                isVerifying
                  ? t('AI_AGENTS.FORM.VERIFYING')
                  : t('AI_AGENTS.FORM.VERIFY_TOKEN')
              }}
            </button>
            <span
              v-if="verifyMessage"
              class="text-xs"
              :class="verifySuccess ? 'text-n-emerald-11' : 'text-n-ruby-9'"
            >
              {{ verifyMessage }}
            </span>
          </div>
        </div>
      </Accordion>
    </div>

    <!-- SECCIÓN: PARÁMETROS -->
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <div class="xl:col-span-2 flex items-center gap-2 mb-1">
        <span class="i-lucide-sliders-horizontal w-4 h-4 text-n-amber-9" />
        <span
          class="text-xs font-bold uppercase tracking-wider text-n-slate-10"
        >
          {{ t('AI_AGENTS.CONFIG.SECTION_PARAMETERS') }}
        </span>
        <div class="flex-1 h-px bg-n-slate-6" />
      </div>

      <Accordion
        icon="i-lucide-flame"
        :title="t('AI_AGENTS.BEHAVIOR.TEMPERATURE_TITLE')"
        :description="t('AI_AGENTS.BEHAVIOR.TEMPERATURE_DESCRIPTION')"
      >
        <label class="grid gap-1">
          <span class="text-sm font-medium">{{
            t('AI_AGENTS.BEHAVIOR.TEMPERATURE_LABEL')
          }}</span>
          <select
            v-model="form.temperature"
            class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
          >
            <option :value="0.1">
              {{ t('AI_AGENTS.BEHAVIOR.TEMPERATURE_VERY_PRECISE') }}
            </option>
            <option :value="0.3">
              {{ t('AI_AGENTS.BEHAVIOR.TEMPERATURE_BALANCED') }}
            </option>
            <option :value="0.5">
              {{ t('AI_AGENTS.BEHAVIOR.TEMPERATURE_CONVERSATIONAL') }}
            </option>
            <option :value="0.7">
              {{ t('AI_AGENTS.BEHAVIOR.TEMPERATURE_CREATIVE') }}
            </option>
            <option :value="1.0">
              {{ t('AI_AGENTS.BEHAVIOR.TEMPERATURE_VERY_CREATIVE') }}
            </option>
          </select>
        </label>
      </Accordion>

      <Accordion
        icon="i-lucide-hash"
        :title="t('AI_AGENTS.CONFIG.MAX_TOKENS_TITLE')"
        :description="t('AI_AGENTS.CONFIG.MAX_TOKENS_DESCRIPTION')"
      >
        <label class="grid gap-1">
          <span class="text-sm font-medium">{{
            t('AI_AGENTS.CONFIG.MAX_TOKENS_LABEL')
          }}</span>
          <input
            v-model.number="form.config.max_tokens"
            type="number"
            min="128"
            max="32768"
            step="128"
            class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 w-32"
          />
          <span class="text-xs text-n-slate-10">{{
            t('AI_AGENTS.CONFIG.MAX_TOKENS_HINT')
          }}</span>
        </label>
      </Accordion>

      <Accordion
        icon="i-lucide-layers"
        :title="t('AI_AGENTS.CATALOG.CATALOG_TYPE_TITLE')"
        :description="t('AI_AGENTS.CATALOG.CATALOG_TYPE_DESCRIPTION')"
        is-open
      >
        <div
          class="flex items-center gap-3 p-3 rounded-lg border border-n-slate-6 bg-n-surface-2"
        >
          <span class="i-lucide-info w-4 h-4 text-n-brand" />
          <div class="text-sm">
            <span class="text-n-slate-11">
              {{ t('AI_AGENTS.CATALOG.CATALOG_TYPE_LABEL') }}
            </span>
            <span class="text-n-slate-11">:</span>
            <span class="font-semibold text-n-slate-12">
              {{ form.catalog_type }}
            </span>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ t('AI_AGENTS.CATALOG.CATALOG_TYPE_HINT') }}
            </p>
          </div>
        </div>
      </Accordion>
    </div>

    <!-- SECCIÓN: RECOMENDACIONES -->
    <div class="grid grid-cols-1 gap-4">
      <div class="flex items-center gap-2 mb-1">
        <span class="i-lucide-table-2 w-4 h-4 text-n-emerald-9" />
        <span
          class="text-xs font-bold uppercase tracking-wider text-n-slate-10"
        >
          {{ t('AI_AGENTS.CONFIG.SECTION_RECOMMENDATIONS') }}
        </span>
        <div class="flex-1 h-px bg-n-slate-6" />
      </div>

      <div class="grid gap-4">
        <div class="grid gap-2">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('OpenAI') }}
          </h3>
          <div class="overflow-x-auto rounded-lg border border-n-slate-6">
            <table class="w-full text-sm">
              <thead class="bg-n-slate-3">
                <tr>
                  <th class="px-3 py-2 text-left font-medium">
                    {{ t('AI_AGENTS.CONFIG.TABLE_TASK') }}
                  </th>
                  <th class="px-3 py-2 text-left font-medium">
                    {{ t('AI_AGENTS.CONFIG.TABLE_MODEL') }}
                  </th>
                  <th class="px-3 py-2 text-left font-medium">
                    {{ t('AI_AGENTS.CONFIG.TABLE_COST') }}
                  </th>
                  <th class="px-3 py-2 text-left font-medium">
                    {{ t('AI_AGENTS.CONFIG.TABLE_SPEED') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-slate-6">
                <tr
                  v-for="(row, idx) in openaiModels"
                  :key="idx"
                  class="hover:bg-n-slate-2"
                >
                  <td class="px-3 py-2">
                    {{ t(`AI_AGENTS.CONFIG.${row.taskKey}`) }}
                  </td>
                  <td class="px-3 py-2 font-medium text-n-brand">
                    {{ row.label }}
                  </td>
                  <td class="px-3 py-2 flex items-center gap-1">
                    <span
                      v-for="n in row.costStars"
                      :key="n"
                      class="i-lucide-star w-3 h-3 text-n-amber-9"
                    />
                    <span class="text-xs text-n-slate-10 ml-1">
                      {{ t(`AI_AGENTS.CONFIG.${row.costLabel}`) }}
                    </span>
                  </td>
                  <td class="px-3 py-2 flex items-center gap-0.5">
                    <span
                      v-for="n in row.speedStars"
                      :key="n"
                      class="i-lucide-star w-3 h-3 text-n-amber-9"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end">
      <button
        class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
        @click="save"
      >
        {{ t('AI_AGENTS.FORM.SAVE') }}
      </button>
    </div>
  </div>
</template>
